/**
 * Renders a text to speech player
 * @prop {string} [className] - Optional css classes
 * @prop {string} id - Unique string for making HTML attributes
 * @prop {string} [label] - Visual label for TTS control buttons
 * @prop {string} text - The text to speak
 */

import React from 'react';
import { observer } from 'mobx-react';

// Translation
import translate from '../translate/Translate';

// Helpers
import { getLocalData, storeLocalData } from '../../helpers';

// Components
import Tooltip from './Tooltip';
import Button from './Button';

// Icons
import * as icons from '../ui/Icons';

const TextToSpeech = observer(class TextToSpeech extends React.Component {
  constructor() {
    super();

    // Create Refs
    this.playButton = React.createRef();

    // Set the initial state
    this.state = {
      paused: false,
      speaking: false
    };

    // Create synthesizer
    this._synth = window.speechSynthesis;
    this._isMounted = false;
    this._spoken = false;
  }

  componentDidMount() {
    this._isMounted = true;

    const { text } = this.props;

    this._utterance = new SpeechSynthesisUtterance(text);
    this._emptyUtterance = new SpeechSynthesisUtterance(" ");

    this._utterance.onend = () => {
      this._synth.cancel();

      if(this._isMounted) {
        this.setState({
          paused: false,
          speaking: false
        });
      }
    }
    this._synth.cancel();

    // Check for stored tts rate
    this.handleStoredRate();

    this.playButton.current.focus();
  }
  
  componentWillUnmount() {
    this._isMounted = false;

    this._utterance = null;
    this._synth.resume();
    this._synth.cancel();
  }

  handlePlayButton = () => {
    const { text } = this.props;
    const { paused, speaking } = this.state;
    const { UserStore } = this.props.store;
    const { ttsRate, ttsText } = UserStore;

    /**
     * Prevent SpeechSynthesis from persisting text from a 
     * different question or browser tab. Check against the 
     * most recent text value that was played.
     */
    const matching = ttsText && text === ttsText;
    const mismatch = ttsText && text !== ttsText;
    const first = !ttsText;

    this._utterance.rate = ttsRate;

    if(speaking) {
      this.pause();
    } else if(paused && matching) {
      this.resume();
    } else if(mismatch || first) {
      this.playNew(text);
    } else {
      this.play(text);
    }
  }

  playNew = (text) => {
    const { UserStore } = this.props.store;
    UserStore.ttsText = text;

    // If this is the first time speaking, initialize it
    // with empty text. Workaround for Chrome bug where TTS
    // will fail to pause after opening a new browser window.
    if (!this._spoken) {
      this._synth.speak(this._emptyUtterance);
      this._spoken = true;
    }
    
    this.setState({
      paused: false,
      speaking: true
    }, () => {
      this._synth.cancel();
      this._synth.speak(this._utterance);
    });
  }

  play = () => {
    this.setState({
      paused: false,
      speaking: true
    }, () => {
      this._synth.speak(this._utterance);
    });
  }

  pause = () => {
    this.setState({
      paused: true,
      speaking: false
    }, () => {
      this._synth.pause();
    })
  }

  stop = () => {
    this.setState({
      paused: false,
      speaking: false
    }, () => {
      this._synth.cancel();
    })
  }

  resume = () => {
    this.setState({
      speaking: true
    }, () => {
      this._synth.resume();
    });
  }

  handleRate = (ttsRate) => {
    const { paused, speaking } = this.state;
    const { UserStore } = this.props.store;

    this._synth.cancel();
    this._utterance.rate = ttsRate;

    if(speaking && !paused) {
      this._synth.speak(this._utterance);
    }

    UserStore.ttsRate = ttsRate;
    storeLocalData('ttsRate', ttsRate);
  }

  handleStoredRate = () => {
    const { UserStore } = this.props.store;

    if(getLocalData('ttsRate')) {
      UserStore.ttsRate = getLocalData('ttsRate');
      this._utterance.rate = getLocalData('ttsRate');
    }
  }

  render() {
    const { className, id, label, translation, store } = this.props;
    const { speaking } = this.state;
    const { UserStore } = store;

    // Return nothing if speechSynthesis is not supported
    if(!window.speechSynthesis) {
      return null;
    }

    // Create speaking rate options
    const rateOptions = [];

    for(let i = 0.5; i <= 2; i += 0.5) {
      rateOptions.push(i);
    }

    return (
      <div
        id={`tts-root-${id}`}
        className={`tts${className ? ` ${className}` : ''}`}
      >
        {label &&
          <strong className="tts__lbl">{label}</strong>
        }

        <ul className="tts__controls">
          <li>
            <label className="tts__rate select select--sm">
              <select
                value={UserStore.ttsRate}
                onChange={(e) => this.handleRate(parseFloat(e.target.value))}
              >
                {rateOptions.map((rate, i) => (
                  <option
                    value={rate}
                    key={i}
                  >
                    {translation.speed.replace('%rate%', rate)}
                  </option>
                ))}
              </select>
            </label>
          </li>
          <li>
            <Button
              className="btn btn--outline tts__control tts__play"
              onClick={this.stop}
              aria-labelledby={`reset-${id}`}
            >
              <Tooltip
                brief
                content={translation.reset}
                hideLabel
                icon={<icons.undo />}
                id={`reset-${id}`}
              />
            </Button>
          </li>
          <li>
            <Button
              className="btn btn--outline tts__control tts__play"
              onClick={this.stop}
              aria-labelledby={`stop-${id}`}
            >
              <Tooltip
                brief
                content={translation.stop}
                hideLabel
                icon={<icons.stop />}
                id={`stop-${id}`}
              />
            </Button>
          </li>
          <li>
            <Button
              refValue={this.playButton}
              className="btn btn--outline tts__control tts__play"
              onClick={this.handlePlayButton}
              aria-labelledby={`play-${id}`}
            >
              <Tooltip
                brief
                content={!speaking ? translation.play : translation.pause}
                hideLabel
                icon={!speaking ? <icons.play /> : <icons.pause />}
                id={`play-${id}`}
              />
            </Button>
          </li>
        </ul>
      </div>
    )
  }
});

export default translate('TextToSpeech')(TextToSpeech);